/**
 * Onboarding — M8 platform module.
 *
 * Vertical stepper over the go-live checklist (platformService.onboarding —
 * the GET auto-creates the default 9-step checklist server-side). Each step
 * can be marked COMPLETE or SKIPPED; the "Complete onboarding" action is
 * enabled once every step is COMPLETE/SKIPPED.
 */
import { useState } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { platformService } from '../lib/services';
import { useApi, useMutation } from '../lib/hooks';
import { useToast } from '../lib/toast';

const STATUS_META = {
  COMPLETE:    { icon: '✓', color: 'var(--success)',       label: 'Complete' },
  IN_PROGRESS: { icon: '●', color: 'var(--info)',          label: 'In progress' },
  SKIPPED:     { icon: '↷', color: 'var(--warning)',       label: 'Skipped' },
  PENDING:     { icon: '○', color: 'var(--text-tertiary)', label: 'Pending' },
};

const fmtDate = (iso) => formatDateTime(iso, null);

/* The 9 default steps (DEALERSHIP_DETAILS … GO_LIVE) as a mock checklist. */
const MOCK_CHECKLIST = {
  currentStep: 'CHANNELS_NUMBERS',
  startedAt: '2026-07-01T00:00:00Z',
  completedAt: null,
  steps: [
    { key: 'DEALERSHIP_DETAILS', label: 'Dealership details', status: 'COMPLETE',    completedAt: '2026-07-01T02:10:00Z' },
    // `key` is the backend onboarding step id — only the display label is ours.
    { key: 'DMS_CONNECTION',     label: 'Dealer system connection', status: 'COMPLETE', completedAt: '2026-07-02T05:40:00Z' },
    { key: 'LEAD_SOURCES',       label: 'Lead sources',       status: 'COMPLETE',    completedAt: '2026-07-03T01:22:00Z' },
    { key: 'CHANNELS_NUMBERS',   label: 'Channels & numbers', status: 'IN_PROGRESS', completedAt: null },
    { key: 'AI_PERSONA',         label: 'AI persona',         status: 'PENDING',     completedAt: null },
    { key: 'TRADING_HOURS',      label: 'Trading hours',      status: 'PENDING',     completedAt: null },
    { key: 'TEAM_USERS',         label: 'Team & users',       status: 'SKIPPED',     completedAt: '2026-07-04T03:00:00Z' },
    { key: 'TEST_LEAD',          label: 'Test lead',          status: 'PENDING',     completedAt: null },
    { key: 'GO_LIVE',            label: 'Go live',            status: 'PENDING',     completedAt: null },
  ],
};

export default function Onboarding() {
  const { toast } = useToast();
  const [busyKey, setBusyKey] = useState(null);

  const { data: checklist, refetch } = useApi(
    () => platformService.onboarding(),
    { fallback: MOCK_CHECKLIST, deps: [] }
  );

  const updateStep = useMutation(platformService.updateStep);
  const complete   = useMutation(platformService.completeOnboarding);

  const steps = checklist?.steps || [];
  const doneCount = steps.filter((s) => s.status === 'COMPLETE' || s.status === 'SKIPPED').length;
  const progressPct = steps.length === 0 ? 0 : (100 * doneCount) / steps.length;
  const allDone = steps.length > 0 && doneCount === steps.length;
  const alreadyCompleted = !!checklist?.completedAt;

  const onUpdateStep = async (step, status) => {
    setBusyKey(step.key);
    try {
      await updateStep.mutate(step.key, status);
      toast(`"${step.label}" → ${status}`, { variant: 'success' });
      refetch();
    } catch (err) {
      toast(`Update failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusyKey(null);
    }
  };

  const onComplete = async () => {
    try {
      await complete.mutate();
      toast('Onboarding complete — your dealership is live on SpectraIQ. Congratulations!', { variant: 'success', duration: 6000 });
      refetch();
    } catch (err) {
      toast(`Complete failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  return (
    <>
      <TopBar
        crumbs={['Onboarding']}
        right={
          <button
            className="btn small primary"
            disabled={!allDone || alreadyCompleted || complete.mutating}
            title={alreadyCompleted ? 'Onboarding already completed'
              : allDone ? 'Finish onboarding' : 'Every step must be COMPLETE or SKIPPED first'}
            onClick={onComplete}
          >
            {alreadyCompleted ? 'Onboarding completed' : complete.mutating ? 'Completing…' : 'Complete onboarding'}
          </button>
        }
      />
      <div className="app-content">
        <div className="page-header">
          <div>
            <div className="page-header-title">Onboarding</div>
            <div className="page-header-sub">Dealer setup checklist and go-live steps</div>
          </div>
        </div>

        {/* Progress card */}
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="flex-between" style={{ marginBottom: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>GO-LIVE PROGRESS</span>
            <span className="mono text-xs text-muted">
              {doneCount} / {steps.length} steps · {progressPct.toFixed(1)}%
            </span>
          </div>
          <div className="progress-bar" style={{ height: 8 }}>
            <div
              className={`fill ${progressPct >= 100 ? 'success' : 'accent'}`}
              style={{ width: `${progressPct}%`, height: '100%' }}
            />
          </div>
          {checklist?.startedAt && (
            <div className="mono text-xs text-muted" style={{ marginTop: 8 }}>
              started {fmtDate(checklist.startedAt)}
              {alreadyCompleted ? ` · completed ${fmtDate(checklist.completedAt)}` : ''}
            </div>
          )}
        </div>

        {/* Vertical stepper */}
        <div className="card" style={{ padding: 0 }}>
          {steps.map((step, i) => {
            const meta = STATUS_META[step.status] || STATUS_META.PENDING;
            const isCurrent = checklist?.currentStep === step.key && !alreadyCompleted;
            const open = step.status !== 'COMPLETE' && step.status !== 'SKIPPED';
            const busy = busyKey === step.key && updateStep.mutating;
            return (
              <div
                key={step.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px',
                  borderBottom: i < steps.length - 1 ? '1px solid var(--border-color)' : 'none',
                  borderLeft: isCurrent ? '3px solid var(--accent-primary)' : '3px solid transparent',
                  background: isCurrent ? 'var(--bg-glass)' : 'transparent',
                }}
              >
                {/* Status icon + connector */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28 }}>
                  <span aria-hidden="true" style={{
                    width: 26, height: 26, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1.5px solid ${meta.color}`, color: meta.color,
                    fontSize: 13, fontWeight: 700,
                  }}>
                    {meta.icon}
                  </span>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {step.label}
                    {isCurrent && (
                      <span className="pill accent filled" style={{ marginLeft: 8 }}>CURRENT</span>
                    )}
                  </div>
                  <div className="mono text-xs" style={{ color: meta.color, marginTop: 2 }}>
                    {meta.label}
                    {step.completedAt ? ` · ${fmtDate(step.completedAt)}` : ''}
                  </div>
                </div>

                {open && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn small primary" disabled={busy}
                            onClick={() => onUpdateStep(step, 'COMPLETE')}>
                      Mark complete
                    </button>
                    <button className="btn small" disabled={busy}
                            onClick={() => onUpdateStep(step, 'SKIPPED')}>
                      Skip
                    </button>
                  </div>
                )}
                {!open && (
                  <button className="btn small" disabled={busy}
                          title="Reopen this step"
                          onClick={() => onUpdateStep(step, 'PENDING')}>
                    Reopen
                  </button>
                )}
              </div>
            );
          })}
          {steps.length === 0 && (
            <div className="empty-state" style={{ padding: 24, textAlign: 'center' }}>
              <div className="text-muted">Checklist is being created…</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
